import { applicationStateLabels } from "./benefit-enums"
import { enumLabel } from "./benefit-format"
import type { BenefitApplicationSummaryDto, BenefitGrantDelivery, BenefitGrantDeliveryMilestone, BenefitGrantReview, BenefitWorkflowResponse } from "./benefit-types"

const workflowStatusLabels: Record<string, string> = {
  Processing: "Processing",
  Completed: "Completed",
  Rescheduled: "Rescheduled",
  Pending: "Pending",
  Returned: "Returned",
  DenialReady: "Denial Ready",
  Certified: "Certified",
  Denied: "Denied",
  Aborted: "Aborted",
  Cancelled: "Cancelled",
  Testing: "Testing",
}

/** Prefer the server display projection without changing or reinterpreting its lifecycle state. */
export function applicationWorkflowLabel(application: BenefitApplicationSummaryDto): string {
  if (application.workflowStatusReadUnavailable) return "Unknown status"
  if (application.workflowLabel) return application.workflowLabel
  if (application.workflowStatus) return workflowStatusLabels[application.workflowStatus] ?? application.workflowStatus
  return enumLabel(applicationStateLabels, application.status)
}

export function returnedRecoveryRequest(
  benefitId: string,
  review: Pick<BenefitGrantReview, "programId" | "programYear" | "version">,
  concurrencyToken: string,
  explanation: string,
  reasonCode: number,
  idempotencyKey: string,
) {
  return {
    path: `/api/benefitapplications/${benefitId}/grants/${review.programId}/${review.programYear}/review/returned-recovery`,
    body: {
      state: "FixRequested",
      reasonGroup: "GrantReview",
      reasonCode,
      explanation,
      concurrencyToken,
      reviewVersion: review.version,
      idempotencyKey,
    },
  }
}

export async function runCorrectionAction<T>(
  execute: () => Promise<T>,
  onUpdated: (updated: T) => void,
  onReload: () => void,
): Promise<T> {
  const updated = await execute()
  onUpdated(updated)
  onReload()
  return updated
}

export function grantReviewActionAvailability(review: BenefitGrantReview) {
  return {
    hasEnabledAction: review.capabilities.some(capability => capability.enabled) ||
      review.replacement?.enabled === true || review.returnedRecovery?.enabled === true,
    canRecoverReturned: review.returnedRecovery?.enabled === true,
  }
}

/** Never infer V2 section access from the colliding compatibility byte. Unknown is read-only. */
export function workflowCanEdit(workflow: BenefitWorkflowResponse | null, section: string, legacyAllowed: boolean): boolean {
  if (workflow?.authority === "Legacy") return legacyAllowed
  return workflow?.authority === "V2" && workflow.sections.some(row => row.section === section && row.canEdit) || false
}

export function workflowReasonGroup(target: string): string | null {
  if (target === "Denied" || target === "DenialReady") return "DenialReadyOrDenied"
  return ["Rescheduled", "Aborted", "Cancelled", "Pending"].includes(target) ? target : null
}

export type DeliveryMilestoneName = "Uploaded" | "Paid"

export interface DeliveryMilestoneDisplay {
  name: DeliveryMilestoneName
  label: string
  status: "included" | "not-included" | "unavailable"
  firstIncludedAt: string | null
}

function displayMilestone(name: DeliveryMilestoneName, milestone: BenefitGrantDeliveryMilestone | null | undefined): DeliveryMilestoneDisplay {
  if (milestone?.status === "Included") {
    return { name, label: name === "Paid" ? "Paid (list inclusion)" : "Uploaded", status: "included", firstIncludedAt: milestone.firstIncludedAt }
  }
  if (milestone?.status === "NotIncluded") {
    return { name, label: "Not included", status: "not-included", firstIncludedAt: null }
  }
  return { name, label: "Unavailable — reconciliation needed", status: "unavailable", firstIncludedAt: null }
}

/** Render only server-owned delivery facts; missing/unknown data never becomes absence. */
export function deliveryReadbackDisplay(delivery: BenefitGrantDelivery | null | undefined): {
  overallLabel: string
  unavailable: boolean
  milestones: DeliveryMilestoneDisplay[]
} {
  const milestones = [displayMilestone("Uploaded", delivery?.uploaded), displayMilestone("Paid", delivery?.paid)]
  const knownOverallStatus = delivery?.status === "NotIncluded" || delivery?.status === "Uploaded" || delivery?.status === "Paid" || delivery?.status === "Unavailable"
  const unavailable = delivery?.status === "Unavailable" || !knownOverallStatus || delivery == null || milestones.some(milestone => milestone.status === "unavailable")
  const overallLabel = unavailable
    ? "Unavailable — reconciliation needed"
    : delivery?.status === "Paid"
      ? "Paid (list inclusion)"
      : delivery?.status === "Uploaded"
        ? "Uploaded"
        : "Not included"
  return { overallLabel, unavailable, milestones }
}
